import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Button,
  Typography,
  Box, Skeleton,
} from '@mui/material';
import { makeStyles } from "@mui/styles";
import { theme } from "../../../theme/theme";
import SubscribeModal from "./SubscribeModal";
import {useGetInternetPackagesQuery, useGetInternetProRataQuery} from "../../../redux/residentPortalApi";
import { formatCurrency } from '../../../utils/currencyUtil';

const useStyles = makeStyles({
  sectionTitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
});

const ChooseInternetPackage: React.FC<{ propertyId: string }> = ({ propertyId }) => {
  const classes = useStyles();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const {data: packages, isLoading: packagesAreLoading} = useGetInternetPackagesQuery();
  const { data: proRata, isFetching: proRataLoading, isError: proRataError } = useGetInternetProRataQuery(
    { propertyId, packageId: selectedPackage || undefined },
    { skip: !selectedPackage }
  );

  // const stripe = useStripe();
  // const elements = useElements();

  // const handleSubscribe = async () => {
  //   if (!stripe || !elements || selectedPackage === null) return;
  //
  //   const cardElement = elements.getElement(CardElement);
  //   if (!cardElement) return;
  //
  //   // 1. Create Payment Method
  //   const { error, paymentMethod } = await stripe.createPaymentMethod({
  //     type: 'card',
  //     card: cardElement,
  //   });
  //
  //   if (error) {
  //     alert(error.message);
  //     return;
  //   }
  //
  //   // 2. Send paymentMethod.id and selected package to backend
  //   try {
  //     const response = await fetch('/api/create-subscription', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({
  //         paymentMethodId: paymentMethod.id,
  //         packageId: packages ? packages.find(pkg => pkg.id === selectedPackage)?.id ?? "" : "",
  //       }),
  //     });
  //
  //     const data = await response.json();
  //     if (data.error) {
  //       alert(data.error);
  //     } else {
  //       alert('Subscription successful!');
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     alert('Something went wrong.');
  //   } finally {
  //   }
  // };

  const handleSubscribeClick = () => {
    if (selectedPackage) {
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box p={4}>
      <Typography variant="h3" className={classes.sectionTitle}>
        Choose Your Internet Package
      </Typography>
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, 1fr)' }} gap={2}>
        {packagesAreLoading ? (
          ['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4'].map((key) => (
            <Card key={key}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={30} />
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="40%" height={30} />
              </CardContent>
            </Card>
          ))
        ) : (
          packages?.map((pkg) => (
            <Card
              key={pkg.id}
              sx={{
                border: selectedPackage === pkg.id ? '2px solid #1976d2' : '1px solid #ccc',
              }}
            >
              <CardActionArea onClick={() => setSelectedPackage(pkg.id || null)}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold">
                    {pkg.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Speed: {pkg.description}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    ${pkg.price}/month
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))
        )}
      </Box>

      {selectedPackage && (
        <Box mt={2} mb={1}>
          {proRataLoading ? (
            <Typography variant="body2" color="textSecondary">Calculating pro‑rata price…</Typography>
          ) : proRataError ? (
            <Typography variant="body2" color="error">Failed to get pro‑rata price. Using full monthly price.</Typography>
          ) : proRata?.proRataCents != null ? (
            <Typography variant="body2">Pro‑rata for this month: <strong>{formatCurrency((proRata.proRataCents || 0) / 100)}</strong></Typography>
          ) : null}
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 1 }}
        disabled={!selectedPackage || proRataLoading}
        onClick={handleSubscribeClick}
      >
        {proRata?.proRataCents != null ? `Subscribe for ${formatCurrency((proRata.proRataCents || 0) / 100)}` : 'Subscribe'}
      </Button>

      <SubscribeModal
        open={modalOpen}
        onClose={handleCloseModal}
        selectedPackageId={selectedPackage ?? ""}
        selectedPackageName={packages && selectedPackage !== null ? 
          packages.find(pkg => pkg.id === selectedPackage)?.name ?? '' : ''}
        propertyId={propertyId}
        amountCents={proRata?.proRataCents != null ? Math.round(proRata.proRataCents) : (packages && selectedPackage !== null ? Math.round((packages.find(pkg => pkg.id === selectedPackage)?.price ?? 0) * 100) : 0)}
      />
    </Box>
  );
};

export default ChooseInternetPackage;
